/**
 * RevenueCat Integration Utility
 * Handles in-app purchases and subscriptions using RevenueCat SDK
 */

import { Capacitor } from "@capacitor/core";

const REVENUECAT_IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const REVENUECAT_ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

let Purchases = null;
let PRODUCT_CATEGORY = null;
let isInitialized = false;

/**
 * Initialize RevenueCat SDK
 * @param {string} appUserId - The user's ID from your app (firebase_uid)
 */
export const initializeRevenueCat = async (appUserId) => {
  console.log("RevenueCat: Starting initialization...");
  console.log("RevenueCat: Platform:", Capacitor.getPlatform());
  console.log("RevenueCat: Is native:", Capacitor.isNativePlatform());
  
  if (!Capacitor.isNativePlatform()) {
    console.log("RevenueCat: Not on native platform, skipping initialization");
    return false;
  }

  // Allow re-initialization if previous attempt failed
  if (isInitialized && Purchases) {
    console.log("RevenueCat: Already initialized, skipping");
    return true;
  }

  try {
    // Dynamically import RevenueCat
    console.log("RevenueCat: Importing SDK...");
    const { Purchases: RC, PRODUCT_CATEGORY: PC } = await import("@revenuecat/purchases-capacitor");
    Purchases = RC;
    PRODUCT_CATEGORY = PC;
    console.log("RevenueCat: SDK imported successfully");

    // Determine API key based on platform
    const platform = Capacitor.getPlatform();
    const apiKey = platform === "ios" ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
    
    console.log("RevenueCat: Platform:", platform);
    console.log("RevenueCat: API Key configured:", apiKey ? `${apiKey.substring(0, 10)}...` : "MISSING");

    if (!apiKey || apiKey === "" || apiKey.startsWith("test_")) {
      console.error("RevenueCat: INVALID API KEY!");
      console.error("RevenueCat: iOS Key:", REVENUECAT_IOS_KEY ? "SET" : "MISSING");
      console.error("RevenueCat: Android Key:", REVENUECAT_ANDROID_KEY ? "SET" : "MISSING");
      return false;
    }

    // Configure RevenueCat
    console.log("RevenueCat: Configuring with user ID:", appUserId || "anonymous");
    try {
      await Purchases.configure({
        apiKey,
        appUserID: appUserId || null,
      });
      isInitialized = true;
      console.log("RevenueCat: Initialized successfully!");
      return true;
    } catch (configError) {
      // If already configured, treat as success
      if (configError?.message?.includes("already configured") || 
          configError?.code === "ALREADY_CONFIGURED") {
        isInitialized = true;
        console.log("RevenueCat: Already configured, marking as initialized");
        return true;
      }
      console.error("RevenueCat: Configuration failed:", configError);
      isInitialized = false;
      return false;
    }
  } catch (error) {
    console.error("RevenueCat: Failed to initialize:", error);
    isInitialized = false;
    return false;
  }
};

/**
 * Get current subscription offerings from RevenueCat
 */
export const getOfferings = async () => {
  console.log("RevenueCat: getOfferings called, isInitialized:", isInitialized);
  
  if (!isInitialized || !Purchases) {
    console.error("RevenueCat: SDK not initialized, cannot get offerings");
    return null;
  }

  try {
    console.log("RevenueCat: Fetching offerings from server...");
    const result = await Purchases.getOfferings();
    console.log("RevenueCat: Raw response keys:", result ? Object.keys(result) : "NULL");
    
    // Handle different SDK response structures:
    // Some versions: { offerings: { current, all } }
    // Some versions: { current, all } directly
    let offerings = result?.offerings || result;
    
    if (!offerings || (!offerings.current && !offerings.all)) {
      console.error("RevenueCat: No offerings in response! Raw:", JSON.stringify(result));
      return null;
    }
    
    console.log("RevenueCat: offerings.current:", offerings.current ? "EXISTS" : "NULL");
    console.log("RevenueCat: offerings.all keys:", offerings.all ? Object.keys(offerings.all) : "NONE");
    
    // If current is null, try to get from 'all'
    if (!offerings.current && offerings.all) {
      const allKeys = Object.keys(offerings.all);
      console.log("RevenueCat: No current offering, checking 'all':", allKeys);
      
      if (offerings.all["default"]) {
        console.log("RevenueCat: Using 'default' offering");
        offerings.current = offerings.all["default"];
      } else if (allKeys.length > 0) {
        console.log("RevenueCat: Using first offering:", allKeys[0]);
        offerings.current = offerings.all[allKeys[0]];
      }
    }
    
    if (offerings.current) {
      console.log("RevenueCat: Final offering:", offerings.current.identifier,
        "packages:", offerings.current.availablePackages?.map(p => p.identifier) || "NONE");
    } else {
      console.error("RevenueCat: Still no current offering after fallback!");
    }
    
    return offerings;
  } catch (error) {
    console.error("RevenueCat: Failed to get offerings:", error);
    return null;
  }
};

/**
 * Get current customer info (subscription status)
 */
export const getCustomerInfo = async () => {
  if (!isInitialized || !Purchases) {
    console.warn("RevenueCat: SDK not initialized");
    return null;
  }

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error("RevenueCat: Failed to get customer info:", error);
    return null;
  }
};

/**
 * Purchase a package
 * @param {object} pkg - The package to purchase from offerings
 */
export const purchasePackage = async (pkg) => {
  if (!isInitialized || !Purchases) {
    throw new Error("RevenueCat SDK not initialized");
  }

  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    window.gtag?.('event', 'purchase', {
      currency: 'AED',
      transaction_id: result.productIdentifier + '_' + Date.now(),
      items: [{ item_name: result.productIdentifier }]
    });
    return {
      success: true,
      customerInfo: result.customerInfo,
      productIdentifier: result.productIdentifier,
    };
  } catch (error) {
    // Check if user cancelled
    if (error.code === "PURCHASE_CANCELLED" || error.userCancelled) {
      return { success: false, cancelled: true };
    }
    console.error("RevenueCat: Purchase failed:", error);
    throw error;
  }
};

/**
 * Purchase a product by ID (for consumables like league slots)
 * @param {string} productId - The product ID to purchase
 */
export const purchaseProduct = async (productId) => {
  if (!isInitialized || !Purchases) {
    throw new Error("RevenueCat SDK not initialized");
  }

  try {
    // Fetch as NON_SUBSCRIPTION (consumable/inapp) — required for Android
    const getProductsOptions = { 
      productIdentifiers: [productId] 
    };
    
    // Add NON_SUBSCRIPTION type if PRODUCT_CATEGORY is available (critical for Android)
    if (PRODUCT_CATEGORY && PRODUCT_CATEGORY.NON_SUBSCRIPTION !== undefined) {
      getProductsOptions.type = PRODUCT_CATEGORY.NON_SUBSCRIPTION;
      console.log("RevenueCat: Fetching product as NON_SUBSCRIPTION:", productId);
    } else {
      console.log("RevenueCat: PRODUCT_CATEGORY not available, fetching without type:", productId);
    }
    
    const { products } = await Purchases.getProducts(getProductsOptions);
    
    if (!products || products.length === 0) {
      console.error("RevenueCat: Product not found:", productId, "- trying as subscription fallback");
      // Fallback: try without type (defaults to subscription)
      const fallback = await Purchases.getProducts({ 
        productIdentifiers: [productId] 
      });
      if (!fallback.products || fallback.products.length === 0) {
        throw new Error(`Product not found: ${productId}`);
      }
      const product = fallback.products[0];
      const result = await Purchases.purchaseStoreProduct({ product });
      window.gtag?.('event', 'in_app_purchase', {
        currency: 'AED',
        transaction_id: productId + '_' + Date.now(),
        items: [{ item_name: productId }]
      });
      return {
        success: true,
        customerInfo: result.customerInfo,
        productIdentifier: productId,
      };
    }
    
    const product = products[0];
    console.log("RevenueCat: Found product:", product.identifier, "Price:", product.priceString);
    const result = await Purchases.purchaseStoreProduct({ product });
    
    window.gtag?.('event', 'in_app_purchase', {
      currency: 'AED',
      transaction_id: productId + '_' + Date.now(),
      items: [{ item_name: productId }]
    });
    return {
      success: true,
      customerInfo: result.customerInfo,
      productIdentifier: productId,
      transactionIdentifier: result.transaction?.transactionIdentifier || null,
    };
  } catch (error) {
    if (error.code === "PURCHASE_CANCELLED" || error.userCancelled) {
      return { success: false, cancelled: true };
    }
    console.error("RevenueCat: Product purchase failed:", error);
    throw error;
  }
};

/**
 * Restore purchases
 */
export const restorePurchases = async () => {
  if (!isInitialized || !Purchases) {
    throw new Error("RevenueCat SDK not initialized");
  }

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo;
  } catch (error) {
    console.error("RevenueCat: Restore failed:", error);
    throw error;
  }
};

/**
 * Check if user has an active subscription
 * @param {object} customerInfo - RevenueCat customer info object
 */
export const hasActiveSubscription = (customerInfo) => {
  if (!customerInfo || !customerInfo.entitlements || !customerInfo.entitlements.active) {
    return false;
  }
  return Object.keys(customerInfo.entitlements.active).length > 0;
};

/**
 * Get the active entitlement ID
 */
export const getActiveEntitlementId = (customerInfo) => {
  if (!hasActiveSubscription(customerInfo)) {
    return null;
  }
  const activeEntitlements = Object.keys(customerInfo.entitlements.active);
  return activeEntitlements[0] || null;
};

/**
 * Map RevenueCat entitlement to app tier
 */
export const mapEntitlementToTier = (entitlementId) => {
  const mapping = {
    pro: "pro",
    pro_plus: "pro_plus",
    pro_max: "pro_max",
    // Add more mappings as needed
  };
  return mapping[entitlementId] || "free";
};

/**
 * Check if running on a native platform
 */
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Add listener for customer info updates
 */
export const addCustomerInfoUpdateListener = (callback) => {
  if (!isInitialized || !Purchases) {
    console.warn("RevenueCat: SDK not initialized");
    return null;
  }

  return Purchases.addCustomerInfoUpdateListener(({ customerInfo }) => {
    callback(customerInfo);
  });
};

export default {
  initializeRevenueCat,
  getOfferings,
  getCustomerInfo,
  purchasePackage,
  purchaseProduct,
  restorePurchases,
  hasActiveSubscription,
  getActiveEntitlementId,
  mapEntitlementToTier,
  isNativePlatform,
  addCustomerInfoUpdateListener,
};
